import { useEffect, useMemo, useState } from 'react';
import { Users, XCircle, Pencil, Trash2, AlertTriangle, PlusCircle, Star, Search, ArrowLeft, Download, Image as ImageIcon } from 'lucide-react';
import WalletCard from './WalletCard';
import PlayerCard from './PlayerCard';
import Pagination from './Pagination';
import { formatCurrency } from '../utils/helpers';
import type { Player, Team, AppUser } from '../types';

interface TeamsViewProps {
  teams: Team[];
  players: Player[];
  currentUser: AppUser;
  onManagePlayer: (player: Player) => void;
  onEditTeam?: (team: Team) => void;
  onDeleteTeam?: (teamId: string) => Promise<boolean>;
  onCreateTeam?: () => void;
  shortlists?: Record<string, string[]>;
  initialSearchQuery?: string;
  showBackButton?: boolean;
  onBack?: () => void;
}

const TeamsView = ({
  teams,
  players,
  currentUser,
  onManagePlayer,
  onEditTeam,
  onDeleteTeam,
  onCreateTeam,
  shortlists = {},
  initialSearchQuery = '',
  showBackButton = false,
  onBack
}: TeamsViewProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [exportingTeamPlayers, setExportingTeamPlayers] = useState(false);
  const [exportingTeamId, setExportingTeamId] = useState<string | null>(null);
  const [exportingPhotos, setExportingPhotos] = useState(false);
  const [exportingTeamPhotosId, setExportingTeamPhotosId] = useState<string | null>(null);
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'sub_admin';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [teamsPerPage, setTeamsPerPage] = useState(9);

  const filteredTeams = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return teams;

    return teams.filter(team => {
      const matchesTeamName = team.name.toLowerCase().includes(query);
      const matchesPlayer = players.some(
        player => player.teamId === team.id && player.name.toLowerCase().includes(query)
      );

      return matchesTeamName || matchesPlayer;
    });
  }, [players, searchQuery, teams]);

  useEffect(() => {
    setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const sanitizeSheetName = (name: string) => {
    const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
    const shortName = cleaned.slice(0, 31);
    return shortName || 'Team';
  };

  const toProperCase = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());

  const sortPlayersByName = (teamPlayers: Player[]) =>
    [...teamPlayers].sort((a, b) => toProperCase(a.name).localeCompare(toProperCase(b.name)));

  const escapeXml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  const columnToLetter = (index: number) => {
    let dividend = index;
    let columnName = '';

    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }

    return columnName;
  };

  const computeColumnWidths = (rows: (string | number)[][]) => {
    const columnWidths: number[] = [];

    rows.forEach(row => {
      row.forEach((cell, index) => {
        const cellLength = String(cell ?? '').length;
        columnWidths[index] = Math.max(columnWidths[index] ?? 0, cellLength);
      });
    });

    return columnWidths.map(length => Math.max(12, Math.min(length + 2, 50)));
  };

  const createSheetXml = (name: string, rows: (string | number)[][]) => {
    const columnWidths = computeColumnWidths(rows);

    const cols = columnWidths
      .map((width, idx) => `<col min="${idx + 1}" max="${idx + 1}" width="${width}" customWidth="1" />`)
      .join('');

    const sheetRows = rows
      .map((row, rowIndex) => {
        const cells = row
          .map((cell, colIndex) => {
            const cellRef = `${columnToLetter(colIndex + 1)}${rowIndex + 1}`;
            const style = rowIndex === 0 ? ' s="1"' : '';
            return `<c r="${cellRef}" t="inlineStr"${style}><is><t>${escapeXml(String(cell ?? ''))}</t></is></c>`;
          })
          .join('');

        return `<row r="${rowIndex + 1}">${cells}</row>`;
      })
      .join('');

    return `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${cols}</cols><sheetData>${sheetRows}</sheetData></worksheet>`;
  };

  const createContentTypesXml = (sheetCount: number) => {
    const sheetOverrides = Array.from({ length: sheetCount })
      .map((_, idx) => `<Override PartName="/xl/worksheets/sheet${idx + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
      .join('');

    return `<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${sheetOverrides}</Types>`;
  };

  const createWorkbookXml = (sheetNames: string[]) => {
    const sheets = sheetNames
      .map((name, idx) => `<sheet name="${escapeXml(name)}" sheetId="${idx + 1}" r:id="rId${idx + 1}"/>`)
      .join('');

    return `<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${sheets}</sheets></workbook>`;
  };

  const createWorkbookRelsXml = (sheetCount: number) => {
    const sheetRels = Array.from({ length: sheetCount })
      .map((_, idx) => `<Relationship Id="rId${idx + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${idx + 1}.xml"/>`)
      .join('');

    return `<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${sheetRels}<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  };

  const createPackageRelsXml = () =>
    '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';

  const createStylesXml = () =>
    '<?xml version="1.0" encoding="UTF-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font><font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFEEF2FF"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs></styleSheet>';

  const crcTable = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let j = 0; j < 8; j++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      table[i] = c >>> 0;
    }
    return table;
  })();

  const crc32 = (data: Uint8Array) => {
    let crc = 0xffffffff;
    for (let i = 0; i < data.length; i++) {
      crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
  };

  const dateToDosDateTime = (date: Date) => {
    const year = date.getFullYear();
    const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
    const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
    return { dosDate, dosTime };
  };

  const concatArrays = (arrays: Uint8Array[]) => {
    const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    arrays.forEach(arr => {
      result.set(arr, offset);
      offset += arr.length;
    });
    return result;
  };

  const createZip = (entries: { name: string; content: string | Uint8Array }[]) => {
    const encoder = new TextEncoder();
    const fileParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;
    const now = dateToDosDateTime(new Date());

    entries.forEach(entry => {
      const nameBytes = encoder.encode(entry.name);
      const dataBytes = typeof entry.content === 'string' ? encoder.encode(entry.content) : entry.content;
      const crc = crc32(dataBytes);

      const localHeader = new Uint8Array(30 + nameBytes.length);
      const localView = new DataView(localHeader.buffer);
      localView.setUint32(0, 0x04034b50, true);
      localView.setUint16(4, 20, true);
      localView.setUint16(6, 0, true);
      localView.setUint16(8, 0, true);
      localView.setUint16(10, now.dosTime, true);
      localView.setUint16(12, now.dosDate, true);
      localView.setUint32(14, crc, true);
      localView.setUint32(18, dataBytes.length, true);
      localView.setUint32(22, dataBytes.length, true);
      localView.setUint16(26, nameBytes.length, true);
      localView.setUint16(28, 0, true);
      localHeader.set(nameBytes, 30);

      fileParts.push(localHeader, dataBytes);

      const centralHeader = new Uint8Array(46 + nameBytes.length);
      const centralView = new DataView(centralHeader.buffer);
      centralView.setUint32(0, 0x02014b50, true);
      centralView.setUint16(4, 20, true);
      centralView.setUint16(6, 20, true);
      centralView.setUint16(8, 0, true);
      centralView.setUint16(10, 0, true);
      centralView.setUint16(12, now.dosTime, true);
      centralView.setUint16(14, now.dosDate, true);
      centralView.setUint32(16, crc, true);
      centralView.setUint32(20, dataBytes.length, true);
      centralView.setUint32(24, dataBytes.length, true);
      centralView.setUint16(28, nameBytes.length, true);
      centralView.setUint16(30, 0, true);
      centralView.setUint16(32, 0, true);
      centralView.setUint16(34, 0, true);
      centralView.setUint16(36, 0, true);
      centralView.setUint16(38, 0, true);
      centralView.setUint32(42, offset, true);
      centralHeader.set(nameBytes, 46);

      centralParts.push(centralHeader);

      offset += localHeader.length + dataBytes.length;
    });

    const centralDirectorySize = centralParts.reduce((sum, arr) => sum + arr.length, 0);
    const endOfCentralDir = new Uint8Array(22);
    const endView = new DataView(endOfCentralDir.buffer);
    endView.setUint32(0, 0x06054b50, true);
    endView.setUint16(4, 0, true);
    endView.setUint16(6, 0, true);
    endView.setUint16(8, entries.length, true);
    endView.setUint16(10, entries.length, true);
    endView.setUint32(12, centralDirectorySize, true);
    endView.setUint32(16, offset, true);
    endView.setUint16(20, 0, true);

    return concatArrays([...fileParts, ...centralParts, endOfCentralDir]);
  };

  const fetchImageAsBuffer = async (url: string): Promise<{ data: Uint8Array | null; error?: string }> => {
    try {
      let fetchUrl = url;

      // Use the Netlify proxy if the URL matches the CloudFront domain
      // This makes the request same-origin, bypassing CORS issues
      if (url.includes('d33z6ewnt0ukqi.cloudfront.net')) {
        const urlObj = new URL(url);
        // /image-proxy/path/to/file.jpg?query=params
        fetchUrl = `/image-proxy${urlObj.pathname}${urlObj.search}`;
      }

      const response = await fetch(fetchUrl);

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      return { data: new Uint8Array(arrayBuffer) };
    } catch (error: any) {
      console.error('Error fetching image:', url, error);
      const errorMsg = error.message || 'Unknown error';
      return {
        data: null,
        error: `${errorMsg} (Type: ${error.name}). Check console for details.`
      };
    }
  };

  const buildWorkbookBuffer = (teamSheets: { name: string; players: Player[] }[]) => {
    const sheets = teamSheets.length ? teamSheets : [{ name: 'Teams', players: [] }];

    const sheetData = sheets.map((team, idx) => {
      const sortedPlayers = sortPlayersByName(team.players);
      const header = ['Player Name', 'Mobile Number', 'Gender', 'Jersey Name', 'Jersey Size', 'Jersey Number', 'Photo URL'];
      const rows = sortedPlayers.length
        ? sortedPlayers.map(player => [
            toProperCase(player.name),
            player.mobile_number || '',
            player.details?.player_gender || '',
            (player.details?.jersey_name || '').toUpperCase(),
            (player.details?.jersey_size || '').toUpperCase(),
            player.details?.jersey_number || '',
            player.details?.photo_url || ''
          ])
        : [['No players', '', '', '', '', '', '']];

      const worksheetRows = [header, ...rows];
      const sheetName = sanitizeSheetName(team.name);
      return { fileName: `sheet${idx + 1}.xml`, name: sheetName, xml: createSheetXml(sheetName, worksheetRows) };
    });

    const contentTypesXml = createContentTypesXml(sheetData.length);
    const workbookXml = createWorkbookXml(sheetData.map(sheet => sheet.name));
    const workbookRelsXml = createWorkbookRelsXml(sheetData.length);
    const packageRelsXml = createPackageRelsXml();
    const stylesXml = createStylesXml();

    const entries = [
      { name: '[Content_Types].xml', content: contentTypesXml },
      { name: '_rels/.rels', content: packageRelsXml },
      { name: 'xl/workbook.xml', content: workbookXml },
      { name: 'xl/_rels/workbook.xml.rels', content: workbookRelsXml },
      { name: 'xl/styles.xml', content: stylesXml },
      ...sheetData.map(sheet => ({ name: `xl/worksheets/${sheet.fileName}`, content: sheet.xml }))
    ];

    return createZip(entries);
  };

  const handleExportTeamPlayers = async () => {
    if (!isAdmin || exportingTeamPlayers || exportingTeamId) return;
    setExportingTeamPlayers(true);

    try {
      const teamSheets = teams.map(team => ({ name: team.name, players: players.filter(player => player.teamId === team.id) }));
      const buffer = await buildWorkbookBuffer(teamSheets.length ? teamSheets : [{ name: 'Teams', players: [] }]);

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `team-players-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExportingTeamPlayers(false);
    }
  };

  const handleExportSingleTeam = async (team: Team) => {
    if (!isAdmin || exportingTeamPlayers || exportingTeamId) return;
    setExportingTeamId(team.id);

    try {
      const teamPlayers = players.filter(player => player.teamId === team.id);
      const buffer = await buildWorkbookBuffer([{ name: team.name, players: teamPlayers }]);

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      const safeName = sanitizeSheetName(team.name).replace(/\s+/g, '-').toLowerCase() || 'team';
      link.href = url;
      link.download = `${safeName}-players-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExportingTeamId(null);
    }
  };

  const handleExportTeamPhotos = async (team: Team) => {
    if (!isAdmin || exportingPhotos || exportingTeamPhotosId) return;
    setExportingTeamPhotosId(team.id);

    try {
      const teamPlayers = players.filter(player => player.teamId === team.id);
      if (teamPlayers.length === 0) return;

      const entries: { name: string; content: string | Uint8Array }[] = [];
      const usedNames = new Map<string, number>();

      for (const player of teamPlayers) {
        const photoUrl = player.details?.photo_url;
        let baseName = toProperCase(player.name).replace(/[^a-zA-Z0-9]/g, '_');

        // Handle duplicate names for image files
        let fileNameBase = baseName;
        if (usedNames.has(baseName)) {
          const count = usedNames.get(baseName)! + 1;
          usedNames.set(baseName, count);
          fileNameBase = `${baseName}_${count}`;
        } else {
          usedNames.set(baseName, 0);
        }

        if (photoUrl) {
          const { data, error } = await fetchImageAsBuffer(photoUrl);

          if (data) {
            entries.push({ name: `${fileNameBase}.jpg`, content: data });
          } else {
            // Add error log for this player
            entries.push({
              name: `${fileNameBase}_error.txt`,
              content: `Failed to download photo for ${player.name}.\nURL: ${photoUrl}\nError: ${error}\nUser Agent: ${navigator.userAgent}`
            });
          }
        } else {
          // Log missing URL
          entries.push({
            name: `${fileNameBase}_no_url.txt`,
            content: `No photo URL found for ${player.name}.`
          });
        }
      }

      if (entries.length === 0) {
        alert('No player data found for this team.');
        return;
      }

      const zipBuffer = createZip(entries);
      const blob = new Blob([zipBuffer], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = sanitizeSheetName(team.name).replace(/\s+/g, '-').toLowerCase() || 'team';
      const timestamp = new Date().toISOString().split('T')[0];

      link.href = url;
      link.download = `${safeName}-photos-${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting photos:', error);
      alert('Failed to export photos. Please try again.');
    } finally {
      setExportingTeamPhotosId(null);
    }
  };

  const handleExportAllPhotos = async () => {
    if (!isAdmin || exportingPhotos || exportingTeamPhotosId) return;
    setExportingPhotos(true);

    try {
      const entries: { name: string; content: string | Uint8Array }[] = [];

      for (const team of teams) {
        const teamPlayers = players.filter(p => p.teamId === team.id);
        if (teamPlayers.length === 0) continue;

        const safeTeamName = sanitizeSheetName(team.name);
        const usedNames = new Map<string, number>();

        for (const player of teamPlayers) {
          const photoUrl = player.details?.photo_url;
          let baseName = toProperCase(player.name).replace(/[^a-zA-Z0-9]/g, '_');

          let fileNameBase = baseName;
          if (usedNames.has(baseName)) {
            const count = usedNames.get(baseName)! + 1;
            usedNames.set(baseName, count);
            fileNameBase = `${baseName}_${count}`;
          } else {
            usedNames.set(baseName, 0);
          }

          if (photoUrl) {
            const { data, error } = await fetchImageAsBuffer(photoUrl);

            if (data) {
              entries.push({ name: `${safeTeamName}/${fileNameBase}.jpg`, content: data });
            } else {
              entries.push({
                name: `${safeTeamName}/${fileNameBase}_error.txt`,
                content: `Failed to download photo for ${player.name}.\nURL: ${photoUrl}\nError: ${error}\nUser Agent: ${navigator.userAgent}`
              });
            }
          } else {
            entries.push({
              name: `${safeTeamName}/${fileNameBase}_no_url.txt`,
              content: `No photo URL found for ${player.name}.`
            });
          }
        }
      }

      if (entries.length === 0) {
        alert('No player data found for any team.');
        return;
      }

      const zipBuffer = createZip(entries);
      const blob = new Blob([zipBuffer], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];

      link.href = url;
      link.download = `all-teams-photos-${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting all photos:', error);
      alert('Failed to export photos. Please try again.');
    } finally {
      setExportingPhotos(false);
    }
  };

  // Paginate teams
  const paginatedTeams = useMemo(() => {
    const startIndex = (currentPage - 1) * teamsPerPage;
    return filteredTeams.slice(startIndex, startIndex + teamsPerPage);
  }, [filteredTeams, currentPage, teamsPerPage]);

  const handleDelete = async (teamId: string) => {
    if (!onDeleteTeam) return;
    setDeleting(true);
    const success = await onDeleteTeam(teamId);
    setDeleting(false);
    if (success) {
      setDeleteConfirm(null);
    }
  };

  if (teams.length === 0) {
    return (
      <div className="text-center py-16">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-600">No Teams Yet</h3>
        <p className="text-slate-400">Create your first team to get started.</p>
        {isAdmin && onCreateTeam && (
          <button
            onClick={onCreateTeam}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            <PlusCircle className="w-4 h-4" /> Add Team
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3 w-full">
          {showBackButton && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Stats
            </button>
          )}
          <div className="relative flex-1 md:flex-none md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search teams or players..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              type="text"
            />
          </div>
        </div>
        {isAdmin && (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleExportTeamPlayers}
              disabled={exportingTeamPlayers || Boolean(exportingTeamId) || exportingPhotos || Boolean(exportingTeamPhotosId)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              <Download className="w-4 h-4" /> {exportingTeamPlayers ? 'Preparing...' : 'Download Team Players'}
            </button>
            <button
              type="button"
              onClick={handleExportAllPhotos}
              disabled={exportingTeamPlayers || Boolean(exportingTeamId) || exportingPhotos || Boolean(exportingTeamPhotosId)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors disabled:opacity-60"
            >
              <ImageIcon className="w-4 h-4" /> {exportingPhotos ? 'Preparing...' : 'Download Photos'}
            </button>
            {onCreateTeam && (
              <button
                onClick={onCreateTeam}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <PlusCircle className="w-4 h-4" /> Add Team
              </button>
            )}
          </div>
        )}
      </div>

      {paginatedTeams.length === 0 && (
        <div className="flex items-center justify-center text-slate-500 bg-white border border-slate-200 rounded-xl py-10">
          <XCircle className="w-5 h-5 mr-2 text-slate-400" />
          <span>No teams match your search.</span>
        </div>
      )}

      {paginatedTeams.map(team => {
        const teamPlayers = players.filter(p => p.teamId === team.id);
        const isConfirmingDelete = deleteConfirm === team.id;
        const shortlistedIds = shortlists[team.id] || [];
        const shortlistedPlayers = players.filter(p => shortlistedIds.includes(p.id) && p.status === 'unsold');

        return (
          <div key={team.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex flex-col md:flex-row md:items-start gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-slate-800">{team.name}</h3>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleExportSingleTeam(team)}
                        disabled={exportingTeamPlayers || Boolean(exportingTeamId) || exportingPhotos || Boolean(exportingTeamPhotosId)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-60"
                      >
                        <Download className="w-4 h-4" />
                        {exportingTeamId === team.id ? 'Preparing...' : 'Download Excel'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExportTeamPhotos(team)}
                        disabled={exportingTeamPlayers || Boolean(exportingTeamId) || exportingPhotos || Boolean(exportingTeamPhotosId)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-sky-700 bg-white border border-sky-200 rounded-lg hover:bg-sky-50 disabled:opacity-60"
                      >
                        <ImageIcon className="w-4 h-4" />
                        {exportingTeamPhotosId === team.id ? 'Preparing...' : 'Download Photos'}
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onEditTeam?.(team)}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit Team"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(team.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Team"
                          disabled={teamPlayers.length > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-slate-500 text-sm">Owned by {team.ownerName}</p>
              </div>
              <div className="w-full md:w-72">
                <WalletCard team={team} />
              </div>
            </div>

            {/* Delete Confirmation */}
            {isConfirmingDelete && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">Delete "{team.name}"?</p>
                    <p className="text-sm text-red-600">This action cannot be undone.</p>
                    {teamPlayers.length > 0 && (
                      <p className="text-sm text-red-700 mt-1 font-medium">
                        ⚠️ Remove all {teamPlayers.length} player(s) first.
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(team.id)}
                        disabled={deleting || teamPlayers.length > 0}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {deleting ? 'Deleting...' : 'Delete Team'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {teamPlayers.length > 0 ? (
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-slate-700">Squad ({teamPlayers.length})</p>
                  <p className="text-sm text-slate-500">
                    Total Spent: <span className="font-bold text-indigo-600">{formatCurrency(teamPlayers.reduce((s, p) => s + p.soldPrice, 0))}</span>
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {teamPlayers.map(player => (
                    <PlayerCard
                      key={player.id}
                      player={player}
                      teams={teams}
                      currentUser={currentUser}
                      onManagePlayer={onManagePlayer}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center text-slate-400 p-6 bg-white rounded-lg border border-slate-200">
                <XCircle className="w-5 h-5 mr-2" /> No players purchased yet.
              </div>
            )}

            {/* Shortlisted Candidates (Admin Only) */}
            {isAdmin && shortlistedPlayers.length > 0 && (
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-yellow-600 fill-yellow-600" />
                  <p className="font-semibold text-yellow-800">Shortlisted Candidates ({shortlistedPlayers.length})</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {shortlistedPlayers.map(player => (
                    <PlayerCard
                      key={player.id}
                      player={player}
                      teams={teams}
                      currentUser={currentUser}
                      onManagePlayer={onManagePlayer}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {filteredTeams.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredTeams.length}
            itemsPerPage={teamsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setTeamsPerPage}
            itemsPerPageOptions={[9, 18, 36, 72]}
          />
        </div>
      )}
    </div>
  );
};

export default TeamsView;
